import { type PropsValue } from 'react-select';

import { type OptionType } from '@hivebrite/design-system/Inputs';
import { ReactSelectInput } from '@hivebrite/design-system/Inputs';

import { useAdaptiveReactSelect } from 'hb-react/shared/components/ReactSelect/hooks/useAdaptiveReactSelect';
import { type ReactSelectAdaptiveProps } from 'hb-react/shared/components/ReactSelect/ReactSelectAdaptive/types';

/**
 * Generic adaptive ReactSelect component that combines useAdaptiveReactSelect hook with ReactSelectInput.
 * This component provides a reusable pattern for creating select inputs with adaptive async behavior, caching and debouncing.
 *
 * Note that the component will use async mode ONLY if needed.
 * If all the existing options fit in a single page of data, the component will act as a normal select
 * and filtering will thus be applied on the frontend side (as per react-select)
 *
 */
const ReactSelectAdaptive = <TQueryFnResponse, TData extends object>({
  dataToOptions,
  debounceTime,
  extractData,
  shouldUseAsync,
  isMulti,
  onChange,
  queryFn,
  queryKey,
  staleTime,
  value,
  ...otherReactSelectProps
}: ReactSelectAdaptiveProps<TQueryFnResponse, TData>) => {
  const {
    defaultOptions,
    isAsync,
    isLoading,
    loadOptions,
    value: valueToUse,
  } = useAdaptiveReactSelect({
    queryKey,
    queryFn,
    extractData,
    shouldUseAsync,
    dataToOptions,
    isMulti,
    value,
    staleTime,
    debounceTime,
  });

  return (
    <ReactSelectInput
      {...otherReactSelectProps}
      defaultOptions={defaultOptions}
      isAsync={isAsync}
      cacheOptions={false}
      options={isAsync ? undefined : defaultOptions}
      isMulti={isMulti}
      isLoading={isLoading}
      loadOptions={loadOptions}
      onChange={onChange as (selectedOption: PropsValue<OptionType>) => void}
      value={valueToUse}
    />
  );
};

export default ReactSelectAdaptive;
